<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { usePageContext } from '@/composables/usePageContext'
import { useStudentScreen } from '@/ui-kit/student2/useStudentScreen'
import { httpPost } from '@/lib/http'
import { dayjs } from '@/lib/dayjs'

import StudentV2StarsInfoModal from '@/ui-kit/student2/StarsInfoModal.vue'
import StudentV2SmartbookLockReason from '@/ui-kit/student2/SmartbookLockReason.vue'
import StudentV2CheckEmailModal from '@/ui-kit/student2/CheckEmailModal.vue'
import StudentTailPassed from '@/ui-kit/student/StudentTailPassed.vue'
import StudentV2OvernightStarsModal from '@/ui-kit/student2/OvernightStarsModal.vue'
import StudentShopModal from '@/ui-kit/student/ShopModal.vue'
import StudentV2ChangeModeModal from '@/ui-kit/student2/ChangeModeModal.vue'
import StudentV2GameModals from '@/ui-kit/student2/GameModals.vue'
import StudentV2ParentHelpButton from '@/ui-kit/student2/ParentHelpButton.vue'
import StudentV2UnitSelect from '@/ui-kit/student2/UnitSelect.vue'
import StudentV2Navigation from '@/ui-kit/student2/Navigation.vue'
import StudentV2Header from '@/ui-kit/student2/Header.vue'
import StudentV2HomePage from '@/ui-kit/student2/Pages/Home.vue'
import StudentV2AudioPage from '@/ui-kit/student2/Pages/Audio.vue'
import StudentV2SongsPage from '@/ui-kit/student2/Pages/Songs.vue'
import StudentV2GamesPage from '@/ui-kit/student2/Pages/Games.vue'
import StudentV2TailsPage from '@/ui-kit/student2/Pages/Tails.vue'
import StudentV2ExternalPage from '@/ui-kit/student2/Pages/ExternalPage.vue'
import StudentV2ParentInfoPage from '@/ui-kit/student2/Pages/ParentInfo.vue'
import AccountLogoutForm from '@/ui-kit/forms/AccountLogoutForm.vue'
import StudentBadBrowserNotification from '@/ui-kit/student/BadBrowserNotification.vue'
import type { Student2Context, Game, Tail } from '@/types/student2'
import SkippedLessonsModal from '~/ui-kit/student2/SkippedLessonsModal.vue'

const DISCHARGE_UPDATE_INTERVAL_SEC = 10

interface StarsInfoModalRef {
  $open: () => void
}

interface CheckEmailModalRef {
  $prompt: (action: string) => Promise<boolean>
}

interface OvernightStarsModalRef {
  $open: () => void
}

interface ShopModalRef {
  $open: (studentId: number, coursePartId: number, tabIndex?: number) => void
  close: () => void
}

interface ChangeModeModalRef {
  $open: () => void
  close: () => void
}

interface GameModalsRef {
  $openGame: (game: Game) => void
  close: () => void
}

interface UnitSelectRef {
  $animateUnitChange: (unitId: number, callback: () => void) => void
}

interface NavigationRef {
  $readLocation: () => void
}

type ExternalPageRef = unknown

interface LogoutFormRef {
  open: (studentId: number) => void
}

const ctx = ref<Student2Context>(usePageContext<Student2Context>())
const { notify } = useNotification()
const { isMobileLocal } = useStudentScreen()

const page = ref('home')
const navHidden = ref(false)
const smartbookLockedModal = ref(false)
const impersonationExpiredModal = ref(false)
const disableLockModal = ref(false)
const animalPicCount = 16
const modalTail = ref<Tail | null>(null)
const currentTime = ref(new Date())

const starsInfoModal = ref<StarsInfoModalRef | null>(null)
const checkEmailModal = ref<CheckEmailModalRef | null>(null)
const overnightStarsModal = ref<OvernightStarsModalRef | null>(null)
const shopModal = ref<ShopModalRef | null>(null)
const changeModeModal = ref<ChangeModeModalRef | null>(null)
const gameModals = ref<GameModalsRef | null>(null)
const unitSelect = ref<UnitSelectRef | null>(null)
const nav = ref<NavigationRef | null>(null)
const externalPage = ref<ExternalPageRef | null>(null)
const logoutForm = ref<LogoutFormRef | null>(null)

let intval: ReturnType<typeof setInterval> | null = null
let dischargeTimeout: ReturnType<typeof setTimeout> | null = null
let freeModeTimeout: ReturnType<typeof setTimeout> | null = null

const unitId = computed(() => {
  return Number(window.location.pathname.split('/').pop())
})

const pageData = computed(() => {
  return { context: ctx.value }
})

const courseInfoPageName = computed(() => {
  switch (ctx.value.course_part.codename) {
    case 'icanspeak':
      return 'speak'
    case 'icanread':
      return 'read'
    case 'icansinggames':
      return 'sing'
  }

  if (ctx.value.course_part.title.startsWith('I Can Sing')) return 'sing'

  return null
})

const modalTailForAnimation = computed(() => {
  if (!modalTail.value) return null

  return {
    jstudent_id: modalTail.value.jstudent_id ?? ctx.value.student.id,
    junit_id: modalTail.value.junit_id,
    tail_type: modalTail.value.tail_type,
    code: Number(modalTail.value.code),
    name: modalTail.value.name ?? modalTail.value.tail_name ?? '',
    junit_name: modalTail.value.junit_name
  }
})

function impersonateExpired() {
  impersonationExpiredModal.value = true
}

const impersonatedUntil = computed(() => {
  if (!ctx.value.impersonated) return null

  const now = dayjs(currentTime.value)
  const expiry = dayjs(Math.floor(ctx.value.impersonated.expiry_timestamp * 1000))
  let diff = expiry.diff(now, 'seconds')

  if (diff <= 0) {
    diff = 0
    impersonateExpired()
  }

  return [Math.floor(diff / 60), diff % 60].map(v => String(v).padStart(2, '0')).join(':')
})

// Watchers
watch(page, () => {
  disableLockModal.value = false
  requestCharge()
})

// Methods
async function onVisibilityChange(): Promise<void> {
  if (document.hidden) return

  try {
    const resp = await httpPost<{
      failed?: boolean
      applied?: number
      star_count?: number
    }>('/student/smart_book/request_apply_night_stars', {
      student_id: ctx.value.student.id,
      course_id: ctx.value.course_part.course_id
    })

    if (resp.failed) throw resp
    if (!resp.applied) return
    if (!ctx.value.smartbook) return

    ctx.value.smartbook.night_stars_applied = resp.applied
    ctx.value.smartbook.star_count = resp.star_count! - resp.applied
    ctx.value.smartbook.awarded_night_stars_count = 0
  } catch (err) {
    console.error(err)
    return
  }

  setTimeout(() => overnightStarsModal.value?.$open(), 500)
}

async function onMessage(event: MessageEvent): Promise<void> {
  if (event.origin !== window.location.origin) return

  switch (event.data.mg_event) {
    case 'change_unit_task_complete': {
      const unitIndex = ctx.value.course_part.units.findIndex(e => e.id === event.data.unit_id)

      const currentVal = ctx.value.course_part.units[unitIndex].all_tasks_completed
      if (currentVal === event.data.all_tasks_completed) return

      unitSelect.value?.$animateUnitChange(event.data.unit_id, () => {
        ctx.value.course_part.units[unitIndex].all_tasks_completed = event.data.all_tasks_completed
      })

      break
    }

    case 'navigate_to_unit': {
      disableLockModal.value = true
      await navigateToUnitReactive(event.data.unit_id, false)
    }
  }
}

async function navigateToUnitReactive(unitIdParam: number, refreshOnFail = true, attempt = 0): Promise<void> {
  // Change unit ID in URL without reloading
  const url = new URL(window.location.href)
  const pathArr = url.pathname.split('/')
  const currentUnitId = pathArr.pop()

  if (unitIdParam.toString() === currentUnitId) return

  pathArr.push(unitIdParam.toString())
  url.pathname = pathArr.join('/')

  const paramsObject = Object.fromEntries(new URLSearchParams(window.location.search).entries())
  window.history.pushState(paramsObject, '', url.toString())
  nav.value?.$readLocation()

  try {
    const { failed, message, context } = await httpPost<{
      failed?: boolean
      message?: string
      context?: string
    }>('/student/request_unit2_context', {
      student_id: ctx.value.student.id,
      unit_id: unitIdParam
    })

    if (failed) throw message

    ctx.value = JSON.parse(context!)
    updateDischarge()
  } catch (err) {
    console.error(`Failed to request context (attempt ${attempt + 1} / 3)`, err)

    if (attempt < 3) {
      return navigateToUnitReactive(unitIdParam, refreshOnFail, attempt + 1)
    } else if (refreshOnFail) {
      window.location.reload()
    }
  }
}

function onHashChange(): void {
  if (window.location.hash.startsWith('#shop')) {
    openShop(window.location.hash.split('=')[1])
  }

  if (window.location.hash.startsWith('#game=')) {
    const gameId = Number(window.location.hash.split('=')[1])
    const game = ctx.value.games.find(e => e.id === gameId)

    if (game && game.unlocked) {
      gameModals.value?.$openGame(game)
    } else if (game) {
      checkEmailModal.value?.$prompt(`приобрести приложение ${game.friendly_name}`).then(isValid => {
        if (!isValid) return
        gameModals.value?.$openGame(game)
      })
    }
  }

  if (window.location.hash === '#change-mode') {
    openModeChangeModal()
  }

  if (window.location.hash === '#parent-cabinet') {
    checkEmailModal.value?.$prompt('перейти в кабинет родителя').then(isValid => {
      if (!isValid) return
      const isParentDemo = (window as { user?: { id: number } }).user?.id === 0
      const isStudentDemo = ctx.value.student.id === 0

      let url = '/users/login?next=/parent/cabinet&tab=login'
      if (isParentDemo === isStudentDemo) url = '/parent/cabinet'

      window.location.href = url
    })
  }

  if (window.location.hash === '#logout') {
    checkEmailModal.value?.$prompt('выйти из аккаунта').then(isValid => {
      if (!isValid) return
      logoutForm.value?.open(ctx.value.student.id)
    })
  }

  if (!window.location.hash) {
    // Modals use internal state management via $open()
    // No explicit close needed — navigating away from hash clears context
  }
}

function openModeChangeModal(): void {
  checkEmailModal.value?.$prompt('сменить режим кабинета ученика').then(isValid => {
    if (!isValid) return

    window.location.hash = 'change-mode'
    changeModeModal.value?.$open()
  })
}

function openShop(tabIndex?: string): void {
  checkEmailModal.value?.$prompt('открыть магазин ученика').then(isValid => {
    if (!isValid) return

    if (!window.location.hash.startsWith('#shop')) {
      window.location.hash = 'shop'
    }

    shopModal.value?.$open(ctx.value.student.id, ctx.value.course_part.id, tabIndex ? Number(tabIndex) : undefined)
  })
}

function onModalClose(): void {
  window.location.hash = ''
}

async function setCabinetMode(newMode: string, isTemporary = false): Promise<void> {
  const studentId = ctx.value.student.id

  try {
    const resp = await httpPost<{
      failed?: boolean
      message?: string
      free_mode_until?: string
    }>('/student/request_set_mode', {
      student_id: studentId,
      mode: newMode,
      hours_free: newMode === 'free' && isTemporary ? 24 : ''
    })

    if (resp.failed) throw resp

    if (newMode === 'free') {
      ctx.value.student.free_mode_until = dayjs(resp.free_mode_until).unix()
    }

    window.location.reload()
  } catch (err) {
    console.error(err)
    let title = 'Произошла неизвестная ошибка при изменении режима кабинета. Повторите попытку позже.'
    if (err && typeof err === 'object' && 'failed' in err && 'message' in err && typeof err.message === 'string')
      title = err.message
    notify({ type: 'error', title })
  }
}

function setDischarge(val: number): void {
  if (ctx.value.smartbook) {
    ctx.value.smartbook.seconds_to_discharge = val
    updateLockStatus()
  }
}

function setSubcharge(val: number): void {
  if (ctx.value.smartbook) {
    ctx.value.smartbook.subcharge_percent = val
    updateLockStatus()
  }
}

function updateLockStatus(runDischargeLoop = true): void {
  if (!ctx.value.smartbook) return

  const canUnlock = ctx.value.smartbook.lock_reason === 'out_of_charge' || !ctx.value.smartbook.lock_reason

  if (ctx.value.student.free_mode && canUnlock) {
    ctx.value.smartbook.unlocked = true
    ctx.value.smartbook.lock_reason = null

    const maxTimeout = 24 * 60 * 60 // 24 hours in seconds
    const timeoutBeforeAuto = ctx.value.student.free_mode_until
      ? dayjs.unix(ctx.value.student.free_mode_until).unix() - dayjs().unix()
      : Infinity

    if (timeoutBeforeAuto <= maxTimeout + 1) {
      freeModeTimeout = setTimeout(() => {
        ctx.value.student.free_mode = false
        ctx.value.student.free_mode_until = null
        requestCharge()
      }, timeoutBeforeAuto * 1000)
    }

    return
  }

  if (ctx.value.smartbook.seconds_to_discharge <= 0) {
    ctx.value.smartbook.seconds_to_discharge = 0

    if (!ctx.value.smartbook.lock_reason) {
      ctx.value.smartbook.unlocked = false
      ctx.value.smartbook.lock_reason = 'out_of_charge'
    }
  }

  if (ctx.value.smartbook.subcharge_percent > 90) {
    requestCharge(false).then(() => {
      if (!ctx.value.smartbook) return
      if (ctx.value.smartbook.subcharge_percent > 10) return
      // if subcharge was reset after reaching 100%

      if (ctx.value.smartbook.lock_reason === 'out_of_charge') {
        ctx.value.smartbook.unlocked = true
        ctx.value.smartbook.lock_reason = null
      }

      if (runDischargeLoop) updateDischarge()
    })
  }

  if (ctx.value.smartbook.lock_reason && externalPage.value && !disableLockModal.value) {
    smartbookLockedModal.value = true
  } else {
    smartbookLockedModal.value = false
  }
}

function updateDischarge(): void {
  if (!ctx.value.smartbook) return
  updateLockStatus(false)
  if (ctx.value.smartbook.lock_reason === 'out_of_charge') return
  if (ctx.value.smartbook.infinite_charge) return

  dischargeTimeout = setTimeout(() => {
    if (!ctx.value.smartbook) return
    const val = ctx.value.smartbook.seconds_to_discharge
    ctx.value.smartbook.seconds_to_discharge = val - DISCHARGE_UPDATE_INTERVAL_SEC

    updateDischarge()
  }, DISCHARGE_UPDATE_INTERVAL_SEC * 1000)
}

async function requestCharge(updateLockStatusParam = true): Promise<void> {
  const studentId = ctx.value.student.id
  const courseId = ctx.value.course_part.course_id

  try {
    const resp = await httpPost<{
      failed?: boolean
      seconds_to_discharge: number
      subcharge_percent: number
      infinite_charge: boolean
    }>('/student/request_get_charge', {
      student_id: studentId,
      course_id: courseId
    })

    if (resp.failed) throw resp

    if (ctx.value.smartbook) {
      ctx.value.smartbook.seconds_to_discharge = resp.seconds_to_discharge
      ctx.value.smartbook.subcharge_percent = resp.subcharge_percent
      ctx.value.smartbook.infinite_charge = resp.infinite_charge
      if (updateLockStatusParam) updateLockStatus()
    }
  } catch (err) {
    console.error('/student/request_get_charge failed:', err)
  }
}

async function onTailDismiss(): Promise<void> {
  if (!ctx.value.student.just_passed_tails) return

  // same as array.shift()
  ctx.value.student.just_passed_tails = ctx.value.student.just_passed_tails.slice(1)

  // unset the tail for a moment so the animation will restart
  modalTail.value = null
  await nextTick()
  modalTail.value = ctx.value.student.just_passed_tails[0] || null
}

function addQuery(url: string, key: string, value: string): string {
  const link = new URL(url, window.location.href)
  link.searchParams.append(key, value)
  return link.href
}

async function exitImpersonation(): Promise<void> {
  try {
    const resp = await httpPost<{ failed?: boolean }>('/student/request_stop_impersonating', {
      student_id: ctx.value.student.id
    })

    if (resp.failed) throw resp
  } catch (err) {
    console.error(err)
    // we'll just let this one slide
  }

  window.close()
  window.location.href = '/journal/performance'
}

// Bound event handlers
const boundOnHashChange = onHashChange.bind(null)
const boundOnMessage = onMessage.bind(null)
const boundOnVisibilityChange = onVisibilityChange.bind(null)

// Lifecycle hooks
onMounted(() => {
  const query = new URLSearchParams(window.location.search)
  page.value = query.get('tab') || 'home'

  navHidden.value = query.get('hidden') === 'true' ? true : query.get('hidden') === 'false' ? false : false

  if (ctx.value.student.just_passed_tails && ctx.value.student.just_passed_tails.length > 0) {
    modalTail.value = ctx.value.student.just_passed_tails[0]
  }

  window.addEventListener('hashchange', boundOnHashChange)
  window.addEventListener('message', boundOnMessage)
  document.addEventListener('visibilitychange', boundOnVisibilityChange)

  // Start the timer for impersonation countdown
  setTimeout(() => {
    intval = setInterval(() => {
      currentTime.value = new Date()
    }, 1000)
  }, Date.now() % 1000)

  updateDischarge()
  onHashChange()

  if (ctx.value.smartbook && ctx.value.smartbook.night_stars_applied) {
    const { smartbook } = ctx.value
    ctx.value.smartbook.star_count = smartbook.star_count - smartbook.night_stars_applied
  }

  setTimeout(() => overnightStarsModal.value?.$open(), 500)
})

onUnmounted(() => {
  window.removeEventListener('hashchange', boundOnHashChange)
  window.removeEventListener('message', boundOnMessage)
  document.removeEventListener('visibilitychange', boundOnVisibilityChange)
  if (intval) clearInterval(intval)
  if (dischargeTimeout) clearTimeout(dischargeTimeout)
  if (freeModeTimeout) clearTimeout(freeModeTimeout)
})
</script>

<template>
  <main
    v-cloak
    id="student-cabinet"
    class="student-cabinet student-cabinet-v2"
    :class="{ 'student-cabinet_impersonated': !!ctx.impersonated }"
  >
    <v-notifications position="bottom right"></v-notifications>
    <StudentBadBrowserNotification></StudentBadBrowserNotification>
    <AccountLogoutForm ref="logoutForm"></AccountLogoutForm>

    <div v-if="ctx.impersonated" role="banner" class="student-cabinet__impersonated-banner">
      Вы находитесь в кабинете ученика {{ ctx.student.last_name }}
      {{ ctx.student.name }}
      <span class="flex-spacer"></span>

      <span class="d-flex align-center">
        <v-icon class="mr-1" :size="isMobileLocal ? 16 : 24">mdi-clock-outline</v-icon>
        Осталось {{ impersonatedUntil }}
      </span>

      <v-button :mode="isMobileLocal ? 'warn rounded xsmall' : 'warn rounded'" @click="exitImpersonation"
        >Выйти</v-button
      >
    </div>

    <!-- OTHER MODALS -->
    <StudentV2StarsInfoModal ref="starsInfoModal"></StudentV2StarsInfoModal>
    <v-rich-modal v-model="impersonationExpiredModal" persistent force-desktop title="Время просмотра истекло">
      <div style="max-width: min(90vw, 450px)">
        <p class="mb-1">
          Вы находились в кабинете ученика:
          <b>{{ ctx.student.last_name }} {{ ctx.student.name }}</b>
        </p>
        <p>
          Режим просмотра ограничен 20 минутами в целях безопасности и защиты данных. Для повторного входа начните новую
          сессию через журнал успеваемости.
        </p>
      </div>

      <template #actions>
        <v-button mode="primary block" @click="exitImpersonation"> Вернуться в журнал </v-button>
      </template>
    </v-rich-modal>
    <v-rich-modal v-model="smartbookLockedModal" class="student-cabinet__smartbook-lock-modal" persistent force-desktop>
      <StudentV2SmartbookLockReason :context="ctx" standalone> </StudentV2SmartbookLockReason>
    </v-rich-modal>
    <StudentV2CheckEmailModal ref="checkEmailModal" :demo="ctx.student.id === 0"></StudentV2CheckEmailModal>
    <StudentTailPassed
      :tail="modalTailForAnimation"
      :animal-pic-count="animalPicCount"
      :student-id="ctx.student.id"
      @dismiss="onTailDismiss"
    ></StudentTailPassed>
    <StudentV2OvernightStarsModal
      ref="overnightStarsModal"
      :context="ctx"
      @add-star="
        () => {
          ctx.smartbook.star_count++
          ctx.smartbook.night_stars_applied--
        }
      "
    ></StudentV2OvernightStarsModal>
    <SkippedLessonsModal />

    <!-- MODALS WITH URL HASH -->
    <StudentShopModal
      ref="shopModal"
      :cabinet-student-id="ctx.student.id"
      :teacher-mode="!!ctx.impersonated"
      @close="onModalClose"
    ></StudentShopModal>
    <StudentV2ChangeModeModal
      ref="changeModeModal"
      :context="ctx"
      @change-mode="setCabinetMode"
      @close="onModalClose"
    ></StudentV2ChangeModeModal>
    <StudentV2GameModals ref="gameModals" :context="ctx" @close="onModalClose"></StudentV2GameModals>

    <!-- PARENT HELP -->
    <transition name="fade">
      <StudentV2ParentHelpButton v-if="!navHidden"></StudentV2ParentHelpButton>
    </transition>

    <StudentV2UnitSelect ref="unitSelect" :context="ctx" :absolute="false"></StudentV2UnitSelect>

    <StudentV2Navigation
      ref="nav"
      :context="ctx"
      @update:tab="
        v => {
          page = v
        }
      "
      @update:hidden="
        v => {
          navHidden = v
        }
      "
      @change-mode="openModeChangeModal"
    ></StudentV2Navigation>
    <StudentV2Header
      ref="header"
      :context="ctx"
      :unit-id="unitId"
      @change-mode="openModeChangeModal"
      @stars-info="() => starsInfoModal?.$open()"
    ></StudentV2Header>

    <!-- PAGES -->
    <transition-group tag="div" name="page-transition" class="student-cabinet__page-transition" mode="out-in">
      <StudentV2HomePage v-if="page === 'home'" key="home" v-bind="pageData" @open-shop="openShop"></StudentV2HomePage>

      <StudentV2AudioPage
        v-if="page === 'audio'"
        key="audio"
        v-bind="pageData"
        @add-subcharge="v => setSubcharge((ctx.smartbook?.subcharge_percent || 0) + v)"
        @update:discharge="setDischarge"
        @update:subcharge="setSubcharge"
        @add-stars="
          awardCount => {
            if (ctx.smartbook) ctx.smartbook.awarded_night_stars_count += awardCount
          }
        "
      ></StudentV2AudioPage>

      <StudentV2SongsPage v-if="page === 'songs'" key="songs" v-bind="pageData"></StudentV2SongsPage>

      <StudentV2GamesPage v-if="page === 'games'" key="games" v-bind="pageData"></StudentV2GamesPage>

      <StudentV2TailsPage
        v-if="page === 'tails'"
        key="tails"
        v-bind="pageData"
        :animal-pic-count="animalPicCount"
      ></StudentV2TailsPage>

      <!-- External pages -->
      <StudentV2ExternalPage
        v-if="page === 'video' && ctx.smartbook"
        ref="externalPage"
        key="video"
        :url="ctx.smartbook.videolessons_url"
        v-bind="pageData"
      ></StudentV2ExternalPage>
      <StudentV2ExternalPage
        v-if="page === 'roundup' && ctx.smartbook"
        ref="externalPage"
        key="roundup"
        :url="ctx.smartbook.workbook_url"
        v-bind="pageData"
      ></StudentV2ExternalPage>
      <StudentV2ExternalPage
        v-if="page === 'avatar' && ctx.smartbook"
        ref="externalPage"
        key="avatar"
        :url="ctx.smartbook.avatar_url"
        v-bind="pageData"
      ></StudentV2ExternalPage>
      <StudentV2ExternalPage
        v-if="page === 'shop' && ctx.smartbook"
        ref="externalPage"
        key="shop"
        :url="addQuery(ctx.smartbook.avatar_url, 'shop', 'true')"
        v-bind="pageData"
      ></StudentV2ExternalPage>

      <!-- PARENT INFO PAGES (html articles) -->
      <StudentV2ParentInfoPage
        v-if="page === 'parent-info' && courseInfoPageName"
        key="parent-info"
        v-bind="pageData"
        :page="courseInfoPageName"
        :context="ctx"
      ></StudentV2ParentInfoPage>
      <StudentV2ParentInfoPage
        v-if="page === 'mode-info'"
        key="mode-info"
        v-bind="pageData"
        page="modes"
        :context="ctx"
      ></StudentV2ParentInfoPage>
    </transition-group>
  </main>
</template>

<style lang="scss">
@use '@/styles/pages/student2/view_unit.scss' as *;

html {
  overflow: hidden;
  overscroll-behavior: none;
}
</style>
